import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';

interface DeleteConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  itemName?: string;
  itemType?: string;
  description?: string;
  questionText?: string;
  warningMessage?: string;
  warningTitle?: string;
  isDeleting?: boolean;
  confirmButtonText?: string;
  cancelButtonText?: string;
}

const DeleteConfirmationDialog: React.FC<DeleteConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  itemName,
  itemType = 'item',
  description,
  questionText,
  warningMessage = 'This action cannot be undone.',
  warningTitle = 'Warning',
  isDeleting = false,
  confirmButtonText = 'Delete',
  cancelButtonText = 'Cancel',
}) => {
  const isArchive = title.toLowerCase().includes('archive') || title.toLowerCase().includes('archiv');
  const actionVerb = isArchive ? 'archive' : 'delete';
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          py: 1.5,
          px: 2.5,
          background: isArchive 
            ? 'linear-gradient(135deg, #6C63FF 0%, #5a52ee 100%)'
            : 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
          color: 'white',
          fontSize: '0.95rem',
          fontWeight: 600,
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            backgroundColor: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.2rem',
          }}
        >
          {isArchive ? '📦' : '🗑️'}
        </Box>
        {title}
      </DialogTitle>
      
      <DialogContent sx={{ px: 3, pb: 2 }}>
        <Box sx={{ mt: 3 }}>
          <Typography 
            variant="body1" 
            sx={{ 
              mb: 2,
              color: 'text.primary',
              fontSize: '0.95rem',
              lineHeight: 1.5,
            }}
          >
            {questionText ? questionText : (
              <>
                Are you sure you want to {actionVerb} {itemName ? (
                  <>
                    this {itemType}: <strong>{itemName}</strong>
                  </>
                ) : (
                  `this ${itemType}`
                )}?
              </>
            )}
          </Typography>

          {description && (
            <Typography 
              variant="body2" 
              sx={{ 
                mb: 2,
                color: 'text.secondary',
                fontSize: '0.85rem',
              }}
            >
              {description}
            </Typography>
          )}

          <Alert 
            severity="warning" 
            sx={{ 
              borderRadius: 2,
              backgroundColor: 'rgba(255, 152, 0, 0.1)',
              '& .MuiAlert-icon': {
                color: 'warning.main',
              }
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
              {warningTitle}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
              {warningMessage}
            </Typography>
          </Alert>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={isDeleting}
          size="small"
          sx={{
            px: 2.5,
            py: 0.75,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.875rem',
            borderColor: 'grey.300',
            color: 'text.secondary',
            '&:hover': {
              borderColor: 'grey.400',
              backgroundColor: 'rgba(0,0,0,0.04)',
            }
          }}
        >
          {cancelButtonText}
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          disabled={isDeleting}
          size="small"
          sx={{
            px: 2.5,
            py: 0.75,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.875rem',
            background: isArchive
              ? 'linear-gradient(135deg, #6C63FF 0%, #5a52ee 100%)'
              : 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
            '&:hover': {
              background: isArchive
                ? 'linear-gradient(135deg, #5a52ee 0%, #4a42dd 100%)'
                : 'linear-gradient(135deg, #ff5252 0%, #e53935 100%)',
            },
            '&:disabled': {
              background: 'rgba(0,0,0,0.12)',
              color: 'rgba(0,0,0,0.26)',
            }
          }}
        >
          {isDeleting ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            confirmButtonText
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteConfirmationDialog;

